import { useState, type ReactNode } from 'react';
import { toggleSort } from '@forecastify/shared';
import {
  AvatarFace,
  AvatarImage,
  Badge,
  DropdownMenu,
  Icon,
  Select,
  SKELETON_WIDTHS,
  Skeleton,
  skeletonWidth,
  Switch,
  Tooltip,
  type IconName,
  type MenuItem,
  Checkbox,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import type { SortState } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C67 · The settings table. The workspace-settings mockup's own, exactly.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A SECOND TABLE IN THE PRODUCT, ON PURPOSE, AND THE FIRST ATTEMPT AT THIS WAS `DataList`.**
 * Round 6 asked for the settings screens to look like `Forecastify - workspace beallitasok
 * (standalone).html`; they were rebuilt on the shared list component instead, and the answer was
 * *"still not correct, looks WAY too different… different sizes, different buttons, tables not
 * unified, edges of the table cut off"* — followed by *"if this means defining a new table type
 * for the settings, so be it"*.
 *
 * ⚠️⚠️ **THE TWO ARE DIFFERENT TABLES BECAUSE THEY ANSWER DIFFERENT QUESTIONS.** `DataList` is a
 * data browser: selection, sorting, saved views, pagination, a server behind it. A settings
 * collection is a dozen rows a person maintains — there is nothing to page, nothing to select in
 * bulk, and sorting a list whose ORDER IS THE SETTING (Kanban columns, priority rank) is wrong
 * rather than missing. What it needs instead is what this has: a per-screen column grid, a drag
 * handle, a cell that edits in place, and a row that is the same height on every settings screen.
 *
 * ⚠️ **CSS grid, not a `<table>`.** Every screen states its own `grid-template-columns` — the
 * mockup's `34px minmax(0,1fr) 96px repeat(7,32px) 52px 92px 36px` and friends — which is what
 * makes seven weekday columns line up with a name column that takes the rest.
 */

/** One cell. The kinds are the mockup's own `C(kind, …)` vocabulary, one to one. */
export type SettingsCell =
  | { kind: 'blank' }
  /** The drag handle. ⚠️ Only draws when the list is reorderable. */
  | { kind: 'drag' }
  /** A row number. Muted, tabular. */
  | { kind: 'index'; text: string | number }
  /**
   * The row's subject: an optional dot, icon or avatar, the name, and an optional second line.
   *
   * ⚠️ `sub` is the mockup's `hasSub` (plain) and `key` its `hasKey` (mono) — a head count reads
   * as prose, a machine identifier reads as itself.
   */
  | {
      kind: 'name';
      text: string;
      color?: string;
      icon?: IconName;
      /* ⚠️ `src` is the member's photo when there is one — the initials are the fallback. */
      /** ⚠️ C68 · `seed` draws the generated face in place of `initials`. */
      avatar?: { initials: string; color: string; src?: string; seed?: string };
      sub?: string;
      monoSub?: string;
    }
  | { kind: 'text'; text: string | number; className?: string }
  | { kind: 'mono'; text: string | number; className?: string }
  /** A figure. ⚠️ Right-aligned by the cell, not by the column — the mockup does the same. */
  | { kind: 'num'; text: string | number; className?: string }
  /**
   * A value typed straight into the row.
   *
   * ⚠️⚠️ **BORDERLESS AT REST**, on hover and focus only — the product's standing rule for every
   * input, and the thing that keeps a grid of them reading as a table rather than a form.
   */
  | {
      kind: 'input';
      value: string;
      ariaLabel: string;
      disabled?: boolean;
      onCommit: (next: string) => void;
    }
  | { kind: 'dot'; text: string; color: string }
  | {
      kind: 'badge';
      text: string;
      tone?: 'default' | 'secondary' | 'destructive' | 'outline';
      color?: string;
    }
  | { kind: 'badges'; items: { label: string; tone?: 'secondary' | 'outline' }[] }
  | {
      kind: 'select';
      value: string;
      options: { value: string; label: string }[];
      ariaLabel: string;
      disabled?: boolean;
      onChange: (next: string) => void;
    }
  | {
      kind: 'switch';
      on: boolean;
      ariaLabel: string;
      disabled?: boolean;
      onChange: (next: boolean) => void;
    }
  /** 17 Oct 2026 · a row picked for an action over several — the quota grid's copy. */
  | { kind: 'check'; on: boolean; ariaLabel: string; onChange: (next: boolean) => void }
  /** ⚠️ The row opens something — the index lists (permission levels, metafield entities). */
  | { kind: 'chevron' }
  | { kind: 'kebab'; items: MenuItem[]; ariaLabel: string };

export interface SettingsColumn {
  /** ⚠️ Empty renders an unnamed column — a drag handle, a badge that only some rows carry. */
  label?: string;
  align?: 'right';
  /**
   * ⚠️⚠️ **GIVE A COLUMN A `sortKey` AND ITS HEADER BECOMES A SORT CONTROL.** Omit it and the
   * header is text: a dead button is worse than no button. The screen reads the key with
   * `sortInMemory`, which is right for these lists and wrong for the paged ones — a settings
   * collection is every row there is, so there is no page for a sort to lie about.
   */
  sortKey?: string;
  /** 17 Oct 2026 · a control in place of the label — the quota grid's select-all checkbox. */
  header?: ReactNode;
}

export interface SettingsRow {
  id: string;
  cells: SettingsCell[];
  /** ⚠️ The whole row opens something. A row with a kebab still gets its own click here. */
  onOpen?: () => void;
  draggable?: boolean;
  dragging?: boolean;
  over?: boolean;
  onDragStart?: () => void;
  onDragOver?: (event: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDrop?: (event: React.DragEvent) => void;
}

/** ⚠️ A heading inside the body — the permissions detail's *FELADATOK*, *IDŐLOGOK*. */
export interface SettingsGroup {
  label?: string;
  rows: SettingsRow[];
}

export function SettingsTable({
  grid,
  minWidth,
  columns,
  groups,
  empty,
  loading = false,
  rowHeight,
  sort = [],
  onSortChange,
}: {
  /** The mockup's `grid-template-columns`, verbatim. */
  readonly grid: string;
  /** px. ⚠️ The card refuses to squash below this; the PAGE scrolls, not the card. */
  readonly minWidth: number;
  readonly columns: SettingsColumn[];
  readonly groups: SettingsGroup[];
  readonly empty: { icon: IconName; title: string; body: string; action?: ReactNode };
  /**
   * Until the first answer: skeleton rows in the table's own grid instead of the empty state, which
   * would otherwise tell somebody to add the first one while theirs are arriving. `skeleton.tsx`.
   */
  readonly loading?: boolean;
  /**
   * px. ⚠️⚠️ **THE INDEX LISTS ARE 46px AND THE COLLECTIONS ARE NOT.** The mockup draws a drill
   * index — the metafield entities, the permission levels — as `h-[46px]` rows: they carry one
   * line each and a row that hugged that line would read as a cramped menu. A collection row is
   * sized by its content, because it may carry a name over a second line.
   */
  readonly rowHeight?: number;
  /**
   * ⚠️⚠️ **THE ORDER OF THE ARRAY IS THE PRIORITY**, matching `DataList` and `toggleSort`:
   * the newest click becomes the primary key and the rest keep their places. A second key draws a
   * small rank badge, because a lone "1" beside the only sorted column tells nobody anything.
   */
  readonly sort?: readonly SortState[];
  readonly onSortChange?: (next: readonly SortState[]) => void;
}) {
  const t = useT();
  const hasHead = columns.some((one) => one.label !== undefined && one.label !== '');
  const count = groups.reduce((total, group) => total + group.rows.length, 0);

  return (
    /**
     * ⚠️⚠️ **THE CARD KEEPS ITS BORDER ON ALL FOUR SIDES, WHICH IS THE BUG THIS FIXES.** *"Edges of
     * the table cut off, can't see the border all around."* The rebuilt screens put the horizontal
     * scroll INSIDE the card, so a wide table ran its rows under the card's own right edge. The
     * mockup scrolls the page around the card and gives the card a `min-width`: the border stays
     * whole and the reader scrolls to it.
     */
    <div
      className="bg-card text-card-foreground overflow-hidden rounded-xl border"
      /* F-2 · every settings collection is one selector for the tour. */
      data-tour="settings-table"
      style={{ minWidth: `${String(minWidth)}px` }}
    >
      {!hasHead ? null : (
        <div
          className="bg-muted/40 text-muted-foreground grid items-center gap-2.5 border-b px-3 py-2 text-[11px] font-medium tracking-[0.03em] uppercase"
          style={{ gridTemplateColumns: grid }}
        >
          {columns.map((column, index) => {
            const here = sort.find((one) => one.key === column.sortKey);
            const rank = sort.findIndex((one) => one.key === column.sortKey) + 1;
            const align = column.align === 'right' ? 'text-right' : '';

            if (column.header !== undefined) {
              return (
                <span key={index} className="flex items-center">
                  {column.header}
                </span>
              );
            }

            /* ⚠️ No key, or nobody listening — plain text. */
            if (column.sortKey === undefined || onSortChange === undefined) {
              return (
                /* ⚠️ The index IS the identity here: a column is a position in the grid. */
                <span key={index} className={`truncate ${align}`}>
                  {column.label ?? ''}
                </span>
              );
            }

            return (
              <span key={index} className={`min-w-0 ${align}`}>
                <button
                  type="button"
                  aria-label={t('list.sortBy', { label: column.label ?? '' })}
                  onClick={() => {
                    onSortChange(toggleSort(sort, column.sortKey as string));
                  }}
                  /* ⚠️ `uppercase` and `tracking-wide` restated: preflight resets
                     `text-transform` on a button, so without them a sortable header would drop
                     out of caps beside its neighbours — the bug the alerts table showed. */
                  className={`hover:text-foreground inline-flex max-w-full items-center gap-1 tracking-wide uppercase select-none ${
                    column.align === 'right' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <span className="truncate">{column.label ?? ''}</span>
                  {here === undefined ? null : (
                    <Icon
                      name={here.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
                      size={12}
                    />
                  )}
                  {here !== undefined && sort.length > 1 ? (
                    <span
                      aria-hidden="true"
                      className="bg-muted text-muted-foreground grid size-3.5 shrink-0 place-items-center rounded-[3px] text-[9px] font-semibold tabular-nums"
                    >
                      {rank}
                    </span>
                  ) : null}
                </button>
              </span>
            );
          })}
        </div>
      )}

      {!loading ? null : (
        <>
          {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
            <div
              key={`skeleton-${String(index)}`}
              aria-hidden
              className="grid items-center gap-2.5 border-b px-3 last:border-b-0"
              style={{ gridTemplateColumns: grid, minHeight: `${String(rowHeight ?? 42)}px` }}
            >
              {columns.map((column, at) => (
                <Skeleton
                  key={at}
                  width={skeletonWidth(index, at)}
                  className={column.align === 'right' ? 'ml-auto' : undefined}
                />
              ))}
            </div>
          ))}
          <div role="status" aria-live="polite" className="sr-only">
            {t('state.loading')}
          </div>
        </>
      )}

      {(loading ? [] : groups).map((group, groupIndex) => (
        <div key={group.label ?? groupIndex}>
          {group.label === undefined ? null : (
            <div className="bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))] text-muted-foreground sticky top-0 z-[1] border-b px-3 py-1.5 text-[11px] font-medium tracking-[0.03em] uppercase">
              {group.label}
            </div>
          )}
          {group.rows.map((row) => (
            <Row key={row.id} row={row} grid={grid} height={rowHeight} />
          ))}
        </div>
      ))}

      {count > 0 || loading ? null : (
        <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
          <span className="bg-muted text-muted-foreground grid size-11 place-items-center rounded-xl">
            <Icon name={empty.icon} size={20} />
          </span>
          <div className="mt-1 text-[13px] font-medium">{empty.title}</div>
          <div className="text-muted-foreground max-w-[380px] text-[12px] leading-relaxed">
            {empty.body}
          </div>
          {empty.action === undefined ? null : <div className="mt-2">{empty.action}</div>}
        </div>
      )}
    </div>
  );
}

function Row({
  row,
  grid,
  height,
}: {
  readonly row: SettingsRow;
  readonly grid: string;
  readonly height?: number;
}) {
  /**
   * ⚠️⚠️ **THE ROW IS A `div`, NOT A `button`, AND THE CLICK IS GUARDED.** A row carries a kebab,
   * sometimes a select and sometimes an input; nesting those inside a button is invalid markup and
   * the browser swallows their clicks. So the row listens, and anything interactive inside it
   * stops the event — which is what `closest` below does without every cell having to remember.
   */
  const open = row.onOpen;

  return (
    <div
      role={open === undefined ? undefined : 'button'}
      tabIndex={open === undefined ? undefined : 0}
      draggable={row.draggable}
      onDragStart={row.onDragStart}
      onDragOver={row.onDragOver}
      onDragEnd={row.onDragEnd}
      onDrop={row.onDrop}
      onClick={
        open === undefined
          ? undefined
          : (event) => {
              /* ⚠️ A click that landed on a control inside the row belongs to that control. */
              if ((event.target as HTMLElement).closest('button,input,select,a,[role="switch"]'))
                return;
              open();
            }
      }
      onKeyDown={
        open === undefined
          ? undefined
          : (event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              open();
            }
      }
      className={[
        'group hover:bg-accent/40 focus-visible:ring-ring/50 grid items-center gap-2.5 border-b px-3 py-1.5 transition-colors outline-none last:border-0 focus-visible:ring-2',
        open === undefined ? '' : 'cursor-pointer',
        row.dragging === true ? 'opacity-50' : '',
        row.over === true && row.dragging !== true ? 'bg-accent/60' : '',
      ].join(' ')}
      style={{
        gridTemplateColumns: grid,
        ...(height === undefined ? {} : { height: `${String(height)}px` }),
      }}
    >
      {row.cells.map((cell, index) => (
        /* ⚠️ Same: a cell is a position in the row's grid, and the set never reorders. */
        <CellView key={index} cell={cell} />
      ))}
    </div>
  );
}

function CellView({ cell }: { readonly cell: SettingsCell }): ReactNode {
  const t = useT();

  switch (cell.kind) {
    case 'blank':
      return <span />;

    case 'drag':
      return (
        <Tooltip asChild content={t('settings.reorderHint')}>
          <span className="text-muted-foreground -ml-1 cursor-grab opacity-50 transition-opacity hover:opacity-100">
            <Icon name="grip-vertical" size={14} />
          </span>
        </Tooltip>
      );

    case 'index':
      return <span className="text-muted-foreground text-[12px] tabular-nums">{cell.text}</span>;

    case 'name':
      return (
        <span className="flex min-w-0 items-center gap-2.5">
          {cell.color === undefined ? null : (
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{ background: cell.color }}
            />
          )}
          {cell.icon === undefined ? null : (
            <span className="bg-muted text-muted-foreground grid size-7 shrink-0 place-items-center rounded-md">
              <Icon name={cell.icon} size={14} />
            </span>
          )}
          {cell.avatar === undefined ? null : (
            <span
              aria-hidden
              className="text-on-id relative grid size-6 shrink-0 place-items-center overflow-hidden rounded-full text-[9px] font-semibold"
              style={{ background: cell.avatar.color }}
            >
              <AvatarFace seed={cell.avatar.seed} fallback={cell.avatar.initials} size={24} />
              <AvatarImage src={cell.avatar.src} />
            </span>
          )}
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-[13px] font-medium">{cell.text}</span>
            {cell.sub === undefined ? null : (
              <span className="text-muted-foreground block truncate text-[11px]">{cell.sub}</span>
            )}
            {cell.monoSub === undefined ? null : (
              <span className="text-muted-foreground block truncate font-mono text-[11px]">
                {cell.monoSub}
              </span>
            )}
          </span>
        </span>
      );

    case 'text':
      return <span className={`truncate text-[12.5px] ${cell.className ?? ''}`}>{cell.text}</span>;

    case 'mono':
      return (
        <span className={`truncate text-[12.5px] tabular-nums ${cell.className ?? ''}`}>
          {cell.text}
        </span>
      );

    case 'num':
      return (
        <span className={`truncate text-right text-[12.5px] tabular-nums ${cell.className ?? ''}`}>
          {cell.text}
        </span>
      );

    case 'input':
      return (
        <span className="flex justify-end">
          <InlineInput cell={cell} />
        </span>
      );

    case 'dot':
      return (
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{ background: cell.color }}
          />
          <span className="truncate text-[12.5px]">{cell.text}</span>
        </span>
      );

    case 'badge':
      return (
        <span className="flex min-w-0 items-center gap-1.5">
          <Badge variant={cell.tone ?? 'secondary'}>
            {cell.color === undefined ? null : (
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ background: cell.color }}
              />
            )}
            {cell.text}
          </Badge>
        </span>
      );

    case 'badges':
      return (
        <span className="flex min-w-0 flex-wrap items-center gap-1">
          {cell.items.map((one) => (
            <Badge key={one.label} variant={one.tone ?? 'secondary'}>
              {one.label}
            </Badge>
          ))}
        </span>
      );

    case 'select':
      return (
        <Select
          value={cell.value}
          aria-label={cell.ariaLabel}
          disabled={cell.disabled}
          options={cell.options}
          className="h-[30px] w-full text-[12.5px]"
          onChange={(event) => {
            cell.onChange(event.target.value);
          }}
        />
      );

    case 'switch':
      return (
        <span className="flex justify-end">
          <Switch
            checked={cell.on}
            disabled={cell.disabled}
            label={cell.ariaLabel}
            onCheckedChange={cell.onChange}
          />
        </span>
      );

    case 'check':
      return (
        <span className="flex items-center">
          <Checkbox checked={cell.on} label={cell.ariaLabel} onCheckedChange={cell.onChange} />
        </span>
      );

    case 'chevron':
      return (
        <span className="flex justify-end">
          <Icon name="chevron-right" size={15} className="text-muted-foreground" />
        </span>
      );

    case 'kebab':
      return (
        <span className="flex justify-end">
          <DropdownMenu items={cell.items} width={208} align="end" label={cell.ariaLabel}>
            <button
              type="button"
              aria-label={cell.ariaLabel}
              className="hover:bg-accent text-muted-foreground hover:text-foreground grid size-7 place-items-center rounded-md transition-colors"
            >
              <Icon name="ellipsis" size={15} />
            </button>
          </DropdownMenu>
        </span>
      );
  }
}

/**
 * ⚠️⚠️ **IT HOLDS ITS OWN DRAFT, AND IT HAS TO.** The commit is on blur, so a controlled input
 * reading straight from the row would be reverted by the next render on every keystroke. The row's
 * value is what the draft resets to when somebody else changes the row underneath.
 *
 * ⚠️ **Escape restores, Enter commits, blur commits.** Escape is the one a reader reaches for
 * after mistyping into a grid, and without it the only way out is remembering what was there.
 */
function InlineInput({ cell }: { readonly cell: Extract<SettingsCell, { kind: 'input' }> }) {
  const [draft, setDraft] = useStateFromValue(cell.value);

  return (
    <input
      value={draft}
      disabled={cell.disabled}
      aria-label={cell.ariaLabel}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        if (draft !== cell.value) cell.onCommit(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(cell.value);
          event.currentTarget.blur();
        }
      }}
      className="hover:border-input focus:border-ring focus:bg-background h-7 w-full max-w-[76px] rounded-md border border-transparent bg-transparent px-2 text-right text-[12.5px] tabular-nums transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-60"
    />
  );
}

/** ⚠️ A draft that follows its source when the source changes — `useState` alone would not. */
function useStateFromValue(value: string): [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value);
  }
  return [draft, setDraft];
}
